import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AccessibilityInfo, Image, StyleSheet, View } from 'react-native';
import { useNetInfo } from '@react-native-community/netinfo';
import { useIsFocused } from '@react-navigation/native';
import { TWCAccessibleContent, TWCButton } from '@components';
import axios from 'axios';
import { useRecoilState, useRecoilValue } from 'recoil';

import { assets } from '@assets';
import { apiConfig, DSP_CLIENT_TOKEN_KEY, onboardingTestIDs, Theme } from '@constants';
import { useContextReference, useInterval, useNodeHandle } from '@hooks';
import { navigationService } from '@services';
import {
  onboardingFlowState,
  screenReaderEnabledState,
  userAuthState,
  userEmailState,
  userSettingsState,
} from '@store';

import { RadialGradientBackground } from '../../../components/loading/RadialGradientBackground';
import { TWCText } from '../../../components/primitives/TWCText/TWCText';
import { TWCTextInput } from '../../../components/primitives/TWCTextInput/TWCTextInput';
import { segmentEventTypes } from '../../../components/segment/segmentEventTypes';
import { SegmentIdentifyUser } from '../../../components/segment/SegmentIdentifyUser';
import { segmentTrackEvent } from '../../../components/segment/SegmentTrackEvent';
import { resetDeviceKey } from '../../../helpers/KeyUtils';
import { isAndroid, isWeb, platformName } from '../../../helpers/Platform';
import { setStorageItem } from '../../../helpers/storage';
import { verifyAppInstalled } from '../../../helpers/verifyAppInstalled';
import { fetchAdobeClientToken, getCode, getUserAuth } from '../../../hooks/api/useGetAdobeTVEUserAuth';

const tPath = 'subscriberSignInPage';
const testIDs = onboardingTestIDs.subscriberSignIn;

const showQRCode = false;

const platform = platformName();

const cableAuthSuccess = async userInfo => {
  await SegmentIdentifyUser(userInfo, true);
  await segmentTrackEvent(segmentEventTypes.LOGIN, userInfo);
  await verifyAppInstalled(userInfo);
  navigationService.replace(onboardingTestIDs.pages.successPage);
};

export const SubscriberSignInContent = () => {
  const [userSettings, setUserSettings] = useRecoilState(userSettingsState);
  const [userAuthData, setUserAuthData] = useRecoilState(userAuthState);

  const netInfo = useNetInfo();

  const deviceIP = netInfo?.details?.ipAddress || '0.0.0.0';

  const { data } = userAuthData;

  const { signUp } = useRecoilValue(onboardingFlowState);
  const emailInputReference = useContextReference(testIDs.subscriberSignIn.emailInput);

  const continueButtonReference = useContextReference(testIDs.subscriberSignIn.continueButton);

  const newCodeButtonReference = useContextReference(testIDs.cableSignIn.newCodeButton);

  const subscriberSectionReference = useContextReference(testIDs.subscriberSignIn.subscriberSection);

  const cableSectionReference = useContextReference(testIDs.cableSignIn.cableSection);

  const emailInputHandle = useNodeHandle(emailInputReference);
  const continueButtonHandle = useNodeHandle(continueButtonReference);
  const newCodeButtonHandle = useNodeHandle(newCodeButtonReference);
  const subscriberSectionHandle = useNodeHandle(subscriberSectionReference);
  const cableSectionHandle = useNodeHandle(cableSectionReference);

  const { t } = useTranslation('translation', {
    keyPrefix: tPath,
  });

  const [userEmail, setUserEmail] = useRecoilState(userEmailState);

  const [screenReaderEnabled] = useRecoilState(screenReaderEnabledState);

  const { token, guid, deviceInfoString } = data ?? {};
  const [cableAuth, setCableAuth] = useState(false);
  const [paidAuth, setPaidAuth] = useState(false);
  const [newCode, setNewCode] = useState(null);

  const isPageFocused = useIsFocused();

  const getPaidCode = async () => {
    try {
      const getPaidCodeBody = {
        client_id: apiConfig.AUTH0_CLIENT_ID,
        connection: 'email',
        email: userEmail,
        send: 'code',
        authParams: {
          scope: 'openid profile',
        },
      };

      await axios.post(apiConfig.AUTH0_START_URL, getPaidCodeBody);
      setPaidAuth(true);
      navigationService.navigate(onboardingTestIDs.pages.authVerificationPage);
    } catch (error) {
      console.warn('GET auth0 passwordless/start error:', error);
      navigationService.navigate(onboardingTestIDs.pages.errorPage, { error });
    }
  };

  const checkAuthorization = async () => {
    try {
      await axios.get(`${apiConfig.ADOBE_TVE_CHECKAUTHN_URL}&deviceId=${guid}&deviceType=${platform}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'X-Device-Info': deviceInfoString,
        },
      });
      setCableAuth(true);
    } catch (error) {
      console.warn('GET adobe checkauthn error:', error);
      setCableAuth(false);
    }
  };

  const initiateFinalAuth = async () => {
    try {
      const headers = {
        Authorization: `Bearer ${token}`,
        'X-Device-Info': deviceInfoString,
      };

      if (isAndroid) {
        headers['X-Forwarded-For'] = deviceIP;
      }

      const { data: authData } = await axios.get(
        `${apiConfig.ADOBE_TVE_AUTHORIZE_URL}&deviceId=${guid}&deviceType=${platform}`,
        {
          headers,
        },
      );
      // look up cable provider details from ID (authData.mvpd)

      axios.get(apiConfig.ADOBE_TVE_MVPDLIST_URL).then(async response => {
        const provider = response.data.requestor.mvpds.mvpd.find(mvpd => mvpd.id.value === authData.mvpd);

        const clientToken = await fetchAdobeClientToken(token);

        setStorageItem(DSP_CLIENT_TOKEN_KEY, clientToken);

        const newSettings = { ...userSettings };

        newSettings.isCableSubscriber = true;
        newSettings.cableProviderId = authData.mvpd;
        newSettings.cableProviderName = provider?.displayName?.value;
        newSettings.cableProviderLogoUrl = provider?.logoUrl?.value;
        newSettings.adobeToken = token;
        newSettings.deviceInfoString = deviceInfoString;
        newSettings.subscriptionStatus = 'cable';
        newSettings.dspCustomerToken = clientToken;

        setUserSettings(newSettings);
        cableAuthSuccess(newSettings);
      });
    } catch (error) {
      console.warn('GET adobe authorize error:', error);
      axios.delete(`${apiConfig.ADOBE_TVE_LOGOUT_URL}&deviceId=${guid}&deviceType=${platform}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'X-Device-Info': deviceInfoString,
        },
      });
      setCableAuth(false);
      // Reset all adobe tokens and auth here
      // Otherwise we will keep polling and finding an error due to how adobe polling works
      // Since adobe is tied to the device ID for polling, reset it and refresh the tve auth
      // using the new device ID
      resetDeviceKey();
      const newData = await getUserAuth();

      setUserAuthData({ ...userAuthData, data: newData });

      navigationService.navigate(onboardingTestIDs.pages.errorPage, {
        errorMessage: 'adobeAuthError',
        buttonMessage: 'startFreeTrialButton',
      });
    }
  };

  const getNewCode = async () => {
    const codeResponse = await getCode(token, guid);

    setNewCode(codeResponse.data.code);
  };

  useInterval(
    () => {
      checkAuthorization();
    },
    cableAuth || paidAuth ? null : 10000,
  );

  useEffect(() => {
    if (cableAuth) {
      initiateFinalAuth();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cableAuth]);

  useEffect(() => {
    setPaidAuth(!isPageFocused);
  }, [isPageFocused]);

  useEffect(() => {
    getNewCode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, guid]);

  const noFocusInput = isAndroid ? emailInputHandle : null;
  const noFocusContinueButton = isAndroid ? continueButtonHandle : null;
  const noFocusNewCodeButton = isAndroid ? newCodeButtonHandle : null;

  const codeForAccessibility = newCode?.split('').join(' ') ?? newCode;

  return (
    <RadialGradientBackground>
      <View style={styles.pageWrapper}>
        <View style={styles.pageContentLeft}>
          <Image source={assets.twcLogo} style={styles.logoImage} testID={testIDs.subscriberSignIn.logoImage} />
          <TWCAccessibleContent
            accessibilityHint={t(
              signUp ? 'subscriberSignIn.sectionAccessibilityHintSignUp' : 'subscriberSignIn.sectionAccessibilityHint',
            )}
            accessibilityLabel={t('subscriberSignIn.sectionAccessibilityLabel')}
            accessibilityRole={'text'}
            accessible={screenReaderEnabled}
            disabledScreenreaderFocus={emailInputHandle}
            nextFocusDown={emailInputHandle}
            nextFocusLeft={null}
            nextFocusRight={cableSectionHandle}
            nextFocusUp={null}
            ref={subscriberSectionReference}
            testID={testIDs.subscriberSignIn.subscriberSection}
          >
            <TWCText
              importantForAccessibility={'no'}
              style={styles.subscriberHeadingText}
              testID={testIDs.subscriberSignIn.headingText}
              variant={'boldText54'}
            >
              {t('subscriberSignIn.headingText')}
            </TWCText>
            <TWCText
              importantForAccessibility={'no'}
              style={styles.subscriberSubheadingText}
              testID={testIDs.subscriberSignIn.subheadingText}
              variant={'regularText36Transparent'}
            >
              {t('subscriberSignIn.subheadingText')}
            </TWCText>
          </TWCAccessibleContent>
          <View style={styles.subscriberEmailInputWrapper}>
            <TWCTextInput
              accessibilityHint={t('subscriberSignIn.fieldAccessibilityHint', {
                email: userEmail,
              })}
              accessibilityLabel={t('subscriberSignIn.fieldAccessibilityLabel')}
              borderColorDefault={styles.subscriberEmailInputBorder}
              hideKeyboardInput
              keyboardDismissConfig={{ up: true }}
              keyboardWrapConfig={{ horizontal: true }}
              nextFocusDown={continueButtonHandle}
              nextFocusLeft={noFocusInput}
              nextFocusRight={screenReaderEnabled ? cableSectionHandle : signUp ? noFocusInput : newCodeButtonHandle}
              nextFocusUp={screenReaderEnabled ? subscriberSectionHandle : noFocusInput}
              placeholder={t('subscriberSignIn.emailInputPlaceholderText')}
              placeholderTextColor={Theme.withOpacity(Theme.white, 0.5)}
              ref={emailInputReference}
              returnKeyType={'done'}
              style={styles.subscriberEmailInput}
              testID={testIDs.subscriberSignIn.emailInput}
              value={userEmail}
              onChangeText={text => {
                setUserEmail(text);
              }}
            />
          </View>
          <TWCButton
            accessibilityHint={t(
              signUp ? 'subscriberSignIn.buttonAccessibilityHintSignUp' : 'subscriberSignIn.buttonAccessibilityHint',
              {
                email: userEmail,
              },
            )}
            accessibilityLabel={t('subscriberSignIn.buttonAccessibilityLabel')}
            nextFocusDown={noFocusContinueButton}
            nextFocusLeft={noFocusContinueButton}
            nextFocusRight={
              screenReaderEnabled ? cableSectionHandle : signUp ? noFocusContinueButton : newCodeButtonHandle
            }
            nextFocusUp={emailInputHandle}
            ref={continueButtonReference}
            style={styles.continueButton}
            testID={testIDs.subscriberSignIn.continueButton}
            title={t('subscriberSignIn.continueButtonText')}
            onPress={getPaidCode}
          />
        </View>
        {!signUp && (
          <>
            <View style={styles.pageDivider} />
            <View style={styles.pageContentRight}>
              {showQRCode ? (
                <Image
                  importantForAccessibility={'no'}
                  source={assets.cableQRCode}
                  style={styles.QRImage}
                  testID={testIDs.cableSignIn.QRCodeImage}
                />
              ) : (
                <View style={styles.QREmpty} />
              )}
              <TWCAccessibleContent
                accessibilityHint={t('cableSignIn.accessibilityHint', {
                  code: codeForAccessibility,
                })}
                accessibilityLabel={t('cableSignIn.accessibilityLabel')}
                accessibilityLiveRegion={'assertive'}
                accessibilityRole={'text'}
                accessible={screenReaderEnabled}
                nextFocusDown={newCodeButtonHandle}
                nextFocusLeft={subscriberSectionHandle}
                ref={cableSectionReference}
                testID={testIDs.cableSignIn.cableSection}
                onPress={() => {
                  AccessibilityInfo.announceForAccessibility(
                    t('cableSignIn.announceCode', {
                      code: codeForAccessibility,
                    }),
                  );
                }}
              >
                <TWCText
                  importantForAccessibility={'no'}
                  style={styles.cableHeadingText}
                  testID={testIDs.cableSignIn.headingText}
                  variant={'boldText54'}
                >
                  {t('cableSignIn.headingText')}
                </TWCText>
                <TWCText
                  importantForAccessibility={'no'}
                  style={styles.cableSubheadingText}
                  testID={testIDs.cableSignIn.subheadingText}
                  variant={'regularText36Transparent'}
                >
                  {t('cableSignIn.subheadingText')}
                </TWCText>
                <TWCText
                  importantForAccessibility={'no'}
                  style={styles.activateLink}
                  testID={testIDs.cableSignIn.activateLink}
                  variant={'boldText54'}
                >
                  {t('cableSignIn.activateLink')}
                </TWCText>
                <TWCText
                  importantForAccessibility={'no'}
                  style={styles.enterCodeText}
                  testID={testIDs.cableSignIn.enterCodeText}
                  variant={'regularText36Transparent'}
                >
                  {t('cableSignIn.enterCodeText')}
                </TWCText>
                <TWCText
                  importantForAccessibility={'no'}
                  style={styles.activateCodeText}
                  testID={testIDs.cableSignIn.activateCode}
                  variant={'regularText90'}
                >
                  {newCode}
                </TWCText>
              </TWCAccessibleContent>
              <TWCButton
                accessibilityHint={t('cableSignIn.newCodeAccessibilityHint')}
                accessibilityLabel={t('cableSignIn.newCodeAccessibilityLabel', {
                  code: codeForAccessibility,
                })}
                nextFocusDown={noFocusNewCodeButton}
                nextFocusLeft={screenReaderEnabled ? emailInputHandle : continueButtonHandle}
                nextFocusRight={noFocusNewCodeButton}
                nextFocusUp={screenReaderEnabled ? cableSectionHandle : noFocusNewCodeButton}
                ref={newCodeButtonReference}
                style={[styles.newCodeButton, showQRCode ? null : { marginTop: Theme.px(150) }]}
                testID={testIDs.cableSignIn.newCodeButton}
                title={t('cableSignIn.newCodeButtonText')}
                onPress={getNewCode}
              />
            </View>
          </>
        )}
      </View>
    </RadialGradientBackground>
  );
};

const styles = StyleSheet.create({
  QREmpty: {
    marginBottom: Theme.px(290),
  },
  QRImage: {
    height: Theme.px(300),
    marginBottom: Theme.px(23),
    marginTop: Theme.px(116),
    width: Theme.px(300),
  },
  activateCodeText: {
    color: Theme.dodgerLightBlue,
    marginBottom: isWeb ? Theme.px(50) : Theme.px(58),
  },
  activateLink: {
    color: Theme.dodgerLightBlue,
    lineHeight: Theme.px(53),
    marginBottom: Theme.px(12),
  },
  cableHeadingText: {
    color: Theme.white,
    lineHeight: Theme.px(80),
    marginBottom: Theme.px(12),
  },
  cableSubheadingText: {
    color: Theme.whisperWhite,
    lineHeight: Theme.px(48),
    marginBottom: isAndroid ? Theme.px(28) : Theme.px(14),
    width: Theme.px(709),
  },
  continueButton: {
    height: Theme.px(78),
    width: Theme.px(263),
    zIndex: isAndroid ? null : -1,
  },
  enterCodeText: {
    color: Theme.withOpacity(Theme.whisperWhite, 0.7),
    lineHeight: Theme.px(60),
    marginBottom: Theme.px(7),
  },
  logoImage: {
    height: Theme.px(156),
    marginBottom: Theme.px(23),
    marginTop: Theme.px(116),
    width: Theme.px(156),
  },
  newCodeButton: {
    height: Theme.px(78),
    width: Theme.px(348),
  },
  pageContentLeft: {
    marginLeft: Theme.px(120),
  },
  pageContentRight: {
    marginRight: Theme.px(131),
    zIndex: isAndroid ? null : -1,
  },
  pageDivider: {
    backgroundColor: Theme.dodgerLightBlue,
    height: Theme.px(886),
    marginTop: Theme.px(116),
    width: Theme.px(4),
    zIndex: isAndroid ? null : -1,
  },
  pageWrapper: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  subscriberEmailInput: {
    backgroundColor: Theme.withOpacity(Theme.whisperWhite, 0.1),
    color: Theme.white,
    opacity: 1,
    textAlign: 'left',
    width: Theme.px(799),
  },
  subscriberEmailInputBorder: {
    borderColor: Theme.withOpacity(Theme.whisperWhite, 0.4),
  },
  subscriberEmailInputWrapper: {
    marginBottom: Theme.px(367),
    marginTop: Theme.px(53),
  },
  subscriberHeadingText: {
    color: Theme.white,
    lineHeight: Theme.px(72),
    marginBottom: Theme.px(7),
  },
  subscriberSubheadingText: {
    color: Theme.withOpacity(Theme.white, 0.7),
  },
});
